import { encodeDate } from "@/common/utils/date-utils";

export class AccountRoute {
    private static apiUrl = "/account";

    static fetch = (): string => this.apiUrl;
}

export class DestinationsRoute {
    private static apiUrl = (userCode: string): string =>
        `/customers/${userCode}/destinations`;

    static fetchAll = (userCode: string): string => this.apiUrl(userCode);
}

export class BrandsRoute {
    private static apiUrl = (userCode: string): string =>
        `/customers/${userCode}/products/brands`;

    static fetchAll = (userCode: string): string => this.apiUrl(userCode);
}

export class ProductsRoute {
    private static apiUrl = (customerCode: string): string =>
        `/customers/${customerCode}/products`;

    static fetch = (
        customerCode: string,
        productType: string,
        productCode: string,
    ): string =>
        `${this.apiUrl(customerCode)}?id=${productType};${encodeURIComponent(productCode)}`;

    static search = (customerCode: string): string =>
        `${this.apiUrl(customerCode)}/search`;
}

export class ProductTypesRoute {
    private static apiUrl = (customerCode: string): string =>
        `/customers/${customerCode}/products/types`;

    static fetchAll = (customerCode: string): string =>
        this.apiUrl(customerCode);
}

export class ProductGroupsRoute {
    private static apiUrl = (customerCode: string): string =>
        `/customers/${customerCode}/products/groups`;

    static fetchAll = (
        customerCode: string,
        groupId: string,
        productType: string,
        brandCode: string,
    ): string =>
        `${this.apiUrl(customerCode)}/${groupId}/${productType}/${brandCode}`;
}

export class CartItemsRoute {
    private static apiUrl = (customerCode: string, cartId: string): string =>
        `/customers/${customerCode}/carts/${cartId}/items`;

    static fetchAll = (userCode: string, cartId: string): string =>
        this.apiUrl(userCode, cartId);

    static add = (userCode: string, cartId: string): string =>
        this.apiUrl(userCode, cartId);

    static addAll = (userCode: string, cartId: string): string =>
        `${this.apiUrl(userCode, cartId)}/batch`;

    static update = (
        userCode: string,
        cartId: string,
        itemId: number,
    ): string => `${this.apiUrl(userCode, cartId)}/${itemId}`;

    static remove = (
        userCode: string,
        cartId: string,
        itemId: number,
    ): string => `${this.apiUrl(userCode, cartId)}/${itemId}`;
}

export class PromotionsRoute {
    private static apiUrl = (customerCode: string): string =>
        `/customers/${customerCode}/promotions`;

    static fetchSheet = (customerCode: string): string =>
        this.apiUrl(customerCode);
}

export class DocumentsRoute {
    private static apiUrl: string = `/documents`;

    static fetchAll = (): string => this.apiUrl;

    static download = (documentId: number): string =>
        `${this.apiUrl}/${documentId}`;
}

export class MthCalculatorRoute {
    private static apiUrl = "/mthcalculator";

    static fetchAllProducts = () => `${this.apiUrl}/products`;
}

export class MthEstimatesRoute {
    private static apiUrl = (customerCode: string) =>
        `customers/${customerCode}/mthestimates`;

    static fetchAll = (customerCode: string) => `${this.apiUrl(customerCode)}`;

    static fetch = (customerCode: string, mthEstimateId: number) =>
        `${this.apiUrl(customerCode)}/${mthEstimateId}`;

    static downloadPdf = (customerCode: string, mthEstimateId: number) =>
        `${this.apiUrl(customerCode)}/${mthEstimateId}/pdf`;

    static delete = (customerCode: string, mthEstimateId: number) =>
        `${this.apiUrl(customerCode)}/${mthEstimateId}`;

    static update = (customerCode: string, mthEstimateId: number) =>
        `${this.apiUrl(customerCode)}/${mthEstimateId}`;

    static create = (customerCode: string) => `${this.apiUrl(customerCode)}`;
}

export class MthEstimateProductsRoute {
    static fetchAll = () => "mthproducts";

    static fetchGroups = () => `mthproductgroups`;
}

export class EstimateContinuousRoute {
    private static apiUrl = (customerCode: string) =>
        `customers/${customerCode}/estimates`;

    static fetchAll = (customerCode: string) => this.apiUrl(customerCode);

    static delete = (customerCode: string, id: number) =>
        `${this.apiUrl(customerCode)}/${id}`;

    static fetch = (customerCode: string, id: number) =>
        `${this.apiUrl(customerCode)}/${id}`;

    static create = (customerCode: string) => this.apiUrl(customerCode);

    static estimateTemplates = () => `estimatetemplates`;

    static downloadPdf = (customerCode: string, id: number) =>
        `${this.apiUrl(customerCode)}/${id}/pdf`;
}

export class CustomersStatisticsRoute {
    private static apiUrl: string = `/reports/customers`;

    static fetchAll = (): string => this.apiUrl;

    static fetchAllDiscounts = (customerCode: string): string =>
        `${this.apiUrl}/${customerCode}/discounts`;

    static fetchAccounting = (customerCode: string): string =>
        `${this.apiUrl}/${customerCode}`;
}

export class BudgetsStatisticsRoute {
    private static apiUrl: string = `/reports/budget`;

    static fetchAllFlattened = (fromMonth: number, toMonth: number): string =>
        `${this.apiUrl}/flattened/${fromMonth}/${toMonth}`;

    static exportExcel = (fromMonth: number, toMonth: number) =>
        `export/excel/budget/${fromMonth}/${toMonth}`;
}

export abstract class BillingsStatisticsRoute {
    private static readonly apiUrl: string = "/reports/billing";

    static readonly fetchAll = (
        year: number,
        fromMonth: number,
        toMonth: number,
    ) => `${this.apiUrl}/flattened/${year}/${fromMonth}/${toMonth}`;

    static readonly fetchAllByMonth = (
        year: number,
        fromMonth: number,
        toMonth: number,
    ) => `/reports/billingbymonth/flattened/${year}/${fromMonth}/${toMonth}`;

    static exportExcel = (year: number, fromMonth: number, toMonth: number) =>
        `/export/excel/billing/${year}/${fromMonth}/${toMonth}`;
}

export abstract class CustomersBillingsStatisticsRoute {
    private static readonly apiUrl: string = "reports/billingbycustomer";

    static readonly fetchAll = (
        year: number,
        fromMonth: number,
        toMonth: number,
    ) => `${this.apiUrl}/flattened/${year}/${fromMonth}/${toMonth}`;

    static exportExcel = (
        year: number,
        fromMonth: number,
        toMonth: number,
        customerCode?: string,
    ) => {
        const route = `export/excel/billingbycustomer/${year}/${fromMonth}/${toMonth}`;
        if (customerCode === undefined) return route;
        return `${route}/${customerCode}`;
    };
}

export class CommissionsStatisticsRoute {
    private static apiUrl = "reports/commissions";
    static fetch = (dateFrom: string, dateTo: string) =>
        `${this.apiUrl}/${dateFrom}/${dateTo}`;
}

export class BillsStatisticsRoute {
    private static apiUrl = "reports/bills";

    static fetch = (id: string) => `${this.apiUrl}/${id}`;

    static exportExcel = ({
        billNumber,
        accessToken,
    }: {
        billNumber: string;
        accessToken: string;
    }) => {
        const searchParams = new URLSearchParams({ billNumber, accessToken });
        return `/api/statistics/commissions/print-layout?${searchParams}`;
    };
}

export abstract class OrdersStatisticsRoute {
    private static readonly apiUrl: string = "/reports/orderbook";

    static readonly fetchAll = (dateFrom: Date, dateTo: Date) =>
        `${this.apiUrl}/${encodeDate(dateFrom)}/${encodeDate(dateTo)}`;
}

export abstract class CoursesRoute {
    private static readonly apiUrl: string = "/courses";

    static readonly fetchAll = () => `${this.apiUrl}`;

    static readonly fetch = (courseId: number) => `${this.apiUrl}/${courseId}`;
}

export abstract class CourseBookingsRoute {
    private static readonly apiUrl = (courseId: number): string =>
        `/courses/${courseId}/bookings`;

    static readonly fetchAll = (courseId: number) => this.apiUrl(courseId);

    static readonly fetch = (courseId: number, courseBookingId: number) =>
        `${this.apiUrl(courseId)}/${courseBookingId}`;

    static readonly create = (courseId: number) => this.apiUrl(courseId);

    static readonly update = (courseId: number, courseBookingId: number) =>
        `${this.apiUrl(courseId)}/${courseBookingId}`;

    static readonly delete = (courseId: number, courseBookingId: number) =>
        `${this.apiUrl(courseId)}/${courseBookingId}`;

    static readonly confirm = (courseId: number, courseBookingId: number) =>
        `${this.apiUrl(courseId)}/${courseBookingId}/confirm`;
}

export abstract class CourseAttendeesRoute {
    private static readonly apiUrl = (
        courseId: number,
        courseBookingId: number,
    ): string => `/courses/${courseId}/bookings/${courseBookingId}/attendees`;

    static readonly create = (courseId: number, courseBookingId: number) =>
        this.apiUrl(courseId, courseBookingId);

    static readonly update = (
        courseId: number,
        courseBookingId: number,
        courseAttendeeId: number,
    ) => `${this.apiUrl(courseId, courseBookingId)}/${courseAttendeeId}`;

    static readonly delete = (
        courseId: number,
        courseBookingId: number,
        courseAttendeeId: number,
    ) => `${this.apiUrl(courseId, courseBookingId)}/${courseAttendeeId}`;
}

export class ComplaintsRoute {
    private static apiUrl = "/complaints";

    static readonly fetchAll = () => this.apiUrl;
    static readonly fetch = (code: string) => `${this.apiUrl}/${code}`;
}

export class TintometerRoute {
    private static apiUrl = "/tintometers";

    static readonly fetchAll = () => this.apiUrl;
    static readonly fetch = (code: string) => `${this.apiUrl}/${code}`;
}

export class TechnicalReportsRoute {
    private static apiUrl = "/TechnicalReports";

    static readonly fetchAll = () => this.apiUrl;

    static readonly fetch = (technicalReportId: number) =>
        `${this.apiUrl}/${technicalReportId}`;

    static readonly download = (technicalReportId: number) =>
        `${this.apiUrl}/${technicalReportId}/download`;
}

export class CsmCompaniesRoute {
    private static apiUrl = "/companies";

    static readonly fetchAll = () => this.apiUrl;
}

export class TermsConditionsRoute {
    private static apiUrl = (): string => `/arxivar`;

    static readonly fetch = (sageCode: string) =>
        `${this.apiUrl()}/${sageCode}`;

    static readonly hasTermsConditionsToRead = (sageCode: string) =>
        `${this.apiUrl()}/${sageCode}/has-documents-to-read`;

    static readonly flagDocumentAsRead = () =>
        `${this.apiUrl()}/flag-document-as-read`;
}
